import React, { CSSProperties, useContext } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Tooltip } from '@mui/material';
import { TABLE_TYPE_SITNGO, TABLE_TYPE_TOURNAMENT } from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
import { useGridMode } from 'contexts/GridModeContext';
import { useAppSelector } from 'hooks';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useTableSelector } from 'hooks/useTableSelector';

// Per-table seat top-up availability — same gate the single-table header uses.
// Cash tables only (tournaments rebuy/add-on through their own flow), the
// player must be seated, have a bankroll, and the table must have top-up
// headroom (maximumTopUp > 0).
export const shouldShowAddChips = (
  tableType: string | undefined,
  isSeated: boolean,
  userBalance: number,
  maximumTopUp: number
): boolean =>
  isSeated &&
  userBalance >= 1 &&
  tableType !== TABLE_TYPE_TOURNAMENT &&
  tableType !== TABLE_TYPE_SITNGO &&
  Number(maximumTopUp) > 0;

// Add Chips for a SPECIFIC table — multi-table (grid) view only. It renders as
// a compact saloon icon button that sits in the footer's left utility stack,
// directly above the hand-history icon (see TopAction portrait branch). The
// click opens this cell's AddChipsModal via GameContext — NOT the global
// cashier — so the top-up is scoped to the table the icon belongs to. The
// single-table view surfaces Add Chips in the header instead, so this is
// grid-only to avoid duplicating it.
const AddChipsButton = () => {
  const isGridMode = useGridMode();
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const tableType = useTableSelector((table) => table.game.tabletype);
  const maximumTopUp = useTableSelector((table) => table.game.maximumTopUp);
  const userBalance = useAppSelector((state) => state.user.playerBankAmount);
  const { setOpenAddChips } = useContext(GameContext);
  const { setCloseBuyInModal } = useContext(GlobalContext);
  const supportsHover = useSupportsHover();

  // Grid-only; single-table view owns Add Chips in the header.
  if (!isGridMode) return null;
  if (!shouldShowAddChips(tableType, isSeated, userBalance, Number(maximumTopUp))) {
    return null;
  }

  const handleAddChips = () => {
    // Same table-scoped flow the single-table header fires — opens this cell's
    // AddChipsModal rather than redirecting to the cashier. A voluntary top-up
    // must NOT pause the player's turn clock (isTimerOff stays as-is): the
    // engine clock keeps running regardless, and hiding the seat timer here
    // left it stuck off after the modal closed. The busted-rebuy flow still
    // sets isTimerOff via Player.tsx where the in-modal countdown needs it.
    setOpenAddChips(true);
    setCloseBuyInModal(false);
  };

  const hoverHandlers = supportsHover
    ? {
        onMouseEnter: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.color = '#ffb45c';
          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,130,31,0.6)';
          (e.currentTarget as HTMLElement).style.transform = 'scale(1.08)';
        },
        onMouseLeave: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.color = '#d9c79e';
          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(232,130,31,0.3)';
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }
      }
    : {};

  return (
    <Tooltip title='Add Chips'>
      <button
        aria-label='Add chips'
        onClick={handleAddChips}
        style={addChipsIconBtn}
        {...hoverHandlers}>
        <AddCircleOutlineIcon style={addChipsIconSize} />
      </button>
    </Tooltip>
  );
};

export default AddChipsButton;

// Matches the footer's saloon icon buttons (TopAction mobileIconBtn) so the
// Add Chips "+" reads as a sibling of the hand-history icon it stacks above.
const addChipsIconBtn: CSSProperties = {
  width: '44px',
  height: '44px',
  minWidth: '44px',
  minHeight: '44px',
  background: 'linear-gradient(180deg, rgba(74,35,10,0.55), rgba(40,18,6,0.65))',
  border: '1px solid rgba(232,130,31,0.3)',
  borderRadius: '8px',
  color: '#d9c79e',
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  boxShadow: '0 3px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,180,92,0.1)',
  transition: 'transform 0.15s ease, color 0.15s ease, border-color 0.15s ease'
};

const addChipsIconSize: CSSProperties = {
  fontSize: '1.4rem'
};
